import { useState, useCallback, useMemo, useRef } from 'react';
import { useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { notifications } from '@/shared/ui/components/basic/Notifications';
import backendClientsCrud from '@services/housien/clients';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { createClientSchema, type ClientFormData } from './schema';
import { buildPayloadFromFormData, type StoredClientValues } from './buildClientPayload';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Reads the backend's RFC 7807 ``invalid_properties`` map from an unknown
 * error shape, when present.
 *
 * The k4d OpenAPI validator answers a 400 with
 * ``{ detail: "Your data has N error(s)", invalid_properties: { "<path>": [...] } }``.
 * ``detail`` is an opaque English count; the field-level information lives in
 * ``invalid_properties`` keyed by dotted JSON path.
 */
function readInvalidProperties(err: unknown): Record<string, unknown> | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const sources: Array<unknown> = [
    (err as { response?: { data?: { invalid_properties?: unknown } } }).response?.data?.invalid_properties,
    (err as { body?: { invalid_properties?: unknown } }).body?.invalid_properties,
    (err as { invalid_properties?: unknown }).invalid_properties,
  ];
  const found = sources.find((s) => s && typeof s === 'object' && !Array.isArray(s));
  return found as Record<string, unknown> | undefined;
}

/**
 * Maps a dotted field path to a human-readable label, falling back to the raw
 * path when no translation exists.
 */
function labelForField(path: string, t: Translate): string {
  const key = `notifications.saveError.fieldLabels.${path}`;
  const label = t(key);
  return label === key ? path : label;
}

/**
 * Builds a clear, field-level message from the backend's ``invalid_properties``
 * map, naming the offending field(s), when the error carries one.
 *
 * This replaces the opaque, English "Your data has N error(s)" `detail` with a
 * localized sentence that tells the broker exactly which field to fix.
 */
function fieldLevelValidationMessage(err: unknown, t: Translate): string | undefined {
  const invalid = readInvalidProperties(err);
  if (!invalid) return undefined;
  const paths = Object.keys(invalid);
  if (paths.length === 0) return undefined;
  const labels = paths.map((p) => labelForField(p, t));
  if (labels.length === 1) {
    return t('notifications.saveError.invalidField', { field: labels[0] });
  }
  return t('notifications.saveError.invalidFields', { fields: labels.join(', ') });
}

/**
 * Extracts a human-readable error message from an unknown API error shape.
 *
 * A validation 400 (``invalid_properties`` present) is turned into a
 * field-level, localized message naming the invalid field, so the broker never
 * sees the opaque "Your data has N error(s)". Any other error falls back to the
 * backend ``detail``/``message``, then to the generic save-error string.
 */
export function extractApiErrorMessage(err: unknown, fallback: string, t: Translate): string {
  const fieldMessage = fieldLevelValidationMessage(err, t);
  if (fieldMessage) return fieldMessage;
  if (!err || typeof err !== 'object') return fallback;
  const candidates: Array<unknown> = [
    (err as { response?: { data?: { detail?: string } } }).response?.data?.detail,
    (err as { body?: { detail?: string } }).body?.detail,
    (err as { detail?: unknown }).detail,
    (err as { message?: unknown }).message,
  ];
  const found = candidates.find((c) => typeof c === 'string' && c);
  return (found as string) ?? fallback;
}

interface UseClientDrawerFormOptions {
  mode: 'create' | 'edit';
  clientId?: string;
  defaultValues?: Partial<ClientFormData>;
  onSuccess?: () => void;
  /** Invoked when a submit is blocked by validation, with the offending fields,
   * so the caller can reveal them (they may sit in a collapsed section). */
  onValidationFailed?: (errors: FieldErrors<ClientFormData>) => void;
}

/**
 * Shows guard failure notifications.
 */
function notifyGuardFailure(reason: 'invalid' | 'noAgency', t: Translate): void {
  const map = {
    invalid: ['notifications.requiredFields.title', 'notifications.requiredFields.message'],
    noAgency: ['notifications.saveError.title', 'notifications.agencyNotFound.message'],
  } as const;
  const [title, message] = map[reason];
  notifications.show({ type: 'error', title: t(title), message: t(message) });
}

/**
 * Executes the create or update CRUD call.
 */
async function executeClientCrud(
  agencyId: string,
  mode: string,
  clientId: string | undefined,
  data: ClientFormData,
  stored: StoredClientValues,
): Promise<void> {
  const crud = backendClientsCrud(agencyId);
  const body = buildPayloadFromFormData(data, mode === 'edit' ? 'edit' : 'create', stored);
  if (mode === 'edit' && clientId) {
    await crud.update({ id: clientId, body });
  } else {
    await crud.insert({ body });
  }
}

type SubmitDeps = {
  agencyId: string;
  mode: string;
  clientId?: string;
  form: ReturnType<typeof useForm<ClientFormData>>;
  /** What the record held when the form opened (empty on create). */
  stored: StoredClientValues;
  onSuccess?: () => void;
  t: Translate;
  setIsSubmitting: (v: boolean) => void;
};

/**
 * Runs the CRUD save with success/error notifications.
 */
async function runDrawerSubmit(deps: SubmitDeps): Promise<void> {
  deps.setIsSubmitting(true);
  try {
    await executeClientCrud(deps.agencyId, deps.mode, deps.clientId, deps.form.getValues(), deps.stored);
    const msg = deps.mode === 'edit'
      ? deps.t('notifications.success.updated')
      : deps.t('notifications.success.created');
    notifications.show({ type: 'success', title: deps.t('notifications.success.title'), message: msg });
    deps.onSuccess?.();
  } catch (err) {
    notifications.show({
      type: 'error',
      title: deps.t('notifications.saveError.title'),
      message: extractApiErrorMessage(err, deps.t('notifications.saveError.fallback'), deps.t),
    });
  } finally {
    deps.setIsSubmitting(false);
  }
}

const EMPTY_DEFAULTS: ClientFormData = {
  personalInfo: {
    fullName: '', birthDate: '', maritalStatus: '', nationality: '', profession: '',
    gender: '', education: '', jobTitle: '', employer: '', industry: '',
  },
  identity: { personType: 'individual', cpf: '', rg: '', issuingAgency: '', cnpj: '', corporateName: '', tradeName: '' },
  roles: ['demand'],
  primaryBroker: '',
  clientRoles: [],
  capturedBy: '',
  legacyCode: '',
  origin: { channel: '', sourceDetail: '', campaign: '' },
  contacts: [{ type: 'whatsapp', value: '' }],
  address: { street: '', number: '', complement: '', neighborhood: '', city: '', state: '', zipCode: '' },
  financial: { monthlyIncome: '', incomeProof: '' },
  demandProfile: undefined,
  metadata: undefined,
};

/**
 * Hook that wires a react-hook-form instance for the client drawer.
 */
export function useClientDrawerForm({
  mode, clientId, defaultValues, onSuccess, onValidationFailed,
}: UseClientDrawerFormOptions) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation('features/clients');
  const { agency_id: paramAgencyId } = useParams<{ agency_id: string }>();
  const agency = useAgency((state) => state.agency);
  const agencyId = paramAgencyId ?? agency?.id;

  const defaults: ClientFormData = mode === 'edit' && defaultValues
    ? { ...EMPTY_DEFAULTS, ...defaultValues }
    : EMPTY_DEFAULTS;

  // Built from the live translator so messages follow the active language, and
  // memoized so the resolver identity is stable across renders.
  const schema = useMemo(() => createClientSchema(t), [t]);

  const form = useForm<ClientFormData>({
    resolver: zodResolver(schema),
    defaultValues: defaults,
    // `onTouched` surfaces format errors when the user leaves a field instead of
    // holding every complaint until "Salvar Cliente"; `onChange` revalidation
    // then clears the message as soon as the value becomes valid.
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  // `defaultValues` is only honoured by react-hook-form on first mount, and the
  // drawer stays mounted across open/close cycles. Keep the latest defaults in a
  // ref so `resetToDefaults` stays referentially stable for effect deps.
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;

  const resetToDefaults = useCallback(() => {
    form.reset(defaultsRef.current);
  }, [form]);

  // `form.handleSubmit` is used rather than a manual `trigger()` because its
  // invalid branch receives the resolved errors directly. Reading
  // `formState.errors` from a callback is unreliable: it is a subscription
  // proxy and comes back empty when touched outside render.
  const handleSubmit = useCallback(
    () => form.handleSubmit(
      async () => {
        if (!agencyId) return notifyGuardFailure('noAgency', t);
        const stored = mode === 'edit' ? defaultsRef.current : {};
        await runDrawerSubmit({ agencyId, mode, clientId, form, stored, onSuccess, t, setIsSubmitting });
      },
      (errors) => {
        onValidationFailed?.(errors);
        notifyGuardFailure('invalid', t);
      },
    )(),
    [agencyId, mode, clientId, form, onSuccess, onValidationFailed, t],
  );

  return { form, handleSubmit, isSubmitting, resetToDefaults, agencyId };
}
